import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { t } from "i18next";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { StatusCode } from "api/protocols";
import { DeleteEvent, GetEvent, PutEvent } from "api/rpc/2024-04/facilityAdmin/event/hospitalityEvent";
import { GetFacility } from "api/rpc/2024-04/facilityAdmin/facility/facility";
import { GetInvoiceTransaction, GetInvoiceTransactionReceipt } from "api/rpc/2024-04/facilityAdmin/order/invoice";

import { showError, showSuccess } from "redux/actions/ui";
import { IEvent } from "redux/reducers/models/event";
import { useAppDispatch } from "hooks/redux";

import Card from "components/card/Card";
import Page from "components/page/Page";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import DataTable from "../../../../../components/dataTable/DataTable";
import TextEditor from "components/textEditor/textEditor";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { LocaleCurrency } from "helpers/Locale";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { Badge } from "components/badge/Badge";
import { ButtonNew as Button} from "components/buttonNew";
import Checkbox from "components/form/checkbox/Checkbox";
import { IInvoiceTransaction } from "redux/reducers/models/order";

export default function HospitalityEvent() {
  const { eventId } = useParams<{ eventId: string }>();
  const dispatch = useAppDispatch();

  const [event, setEvent] = useState<IEvent>(null);
  const [endDate, setEndDate] = useState(false);

  const history = useHistory();

  useEffect(() => {
    void loadEvent();
  }, []);

  async function loadEvent() {
    const numericEventId = Number(eventId);
    const eventRes = await GetEvent({ id: numericEventId }, false);

    if (eventRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.hospitality.hospitality_event.001")));
      return;
    }

    const event = eventRes.data[0];

    const depositRes = await GetInvoiceTransaction({
      hospitality_event_id: event.id,
    }, true);

    if (depositRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.hospitality.hospitality_event.002")));
      return;
    }

    event.deposits = depositRes.data;

    if (event?.end_date) {
      setEndDate(true);
    }

    setEvent(event);

    const facilityRes = await GetFacility({ id: event.facility_id }, true);

    if (facilityRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.hospitality.hospitality_event.003")));
      return;
    }
  }

  async function updateEvent() {
    const params = {
      id: event.id,
      name: event.name,
      date: event.date,
      end_date: event.end_date,
      description: event.description,
      display_online: event.display_online,
    };

    const eventRes = await PutEvent(params, true);

    if (eventRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.hospitality.hospitality_event.012")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.hospitality.hospitality_event.013")));
    setEvent(eventRes.data);
  }

  async function deleteHospitalityEvent() {
    const res = await DeleteEvent({ id: event.id }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting hospitality event")));
      return;
    }

    dispatch(showSuccess(t("Hospitality event successfully deleted")));
    history.push("/admin/events-hospitality");

  }

  async function getDepositReceipt(transactionId: number) {
    const res = await GetInvoiceTransactionReceipt({ transaction_id: transactionId }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.hospitality.hospitality_event.016")));
      return;
    }

    window.open().document.write(res.data as string);
  }

  const primaryAction = {
    content: t("secure.facility.hospitality.hospitality_event.014"),
    action: updateEvent,
  };

  const secondaryActions = {
    content: t("Delete"),
    action: deleteHospitalityEvent,
  };

  return (
    <Page
      title={event?.name}
      narrow
      breadcrumbs={[
        {
          prefix: true,
          label: t("secure.facility.hospitality.hospitality_event.004"),
          url: "/admin/events-hospitality",
        },
      ]}
      primaryAction={primaryAction}
      secondaryActions={[secondaryActions]}
    >
      <Card title={t("secure.facility.hospitality.hospitality_event.005")}>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                label={t("secure.facility.hospitality.hospitality_event.006")}
                value={event?.name ?? ""}
                onChange={e => setEvent(prev => ({ ...prev, name: e.target.value }))}
              />
            </FormLayout.Group>
            <FormLayout.Group>
            <DatePickerInput
                label={t("Start date")}
                startingDate={new Date(moment(event?.date).format())}
                setStartingDate={(date: Date) =>
                  setEvent(prev => ({ ...prev, date: moment(date).format("YYYY-MM-DD") }))
                }
                months={1}
                position="left"
              />
              {!endDate ? (
                    <Button size="medium" type="secondary" onClick={() => {
                      setEndDate(true);
                      setEvent(prev => ({...prev, end_date:  moment(new Date()).format("YYYY-MM-DD")}));
                    }}>{t("Set end date")}</Button>
                  ) : (
                    <DatePickerInput
                    label={t("End date")}
                    startingDate={new Date(moment(event?.end_date).format())}
                    setStartingDate={(date: Date) =>
                      setEvent(prev => ({ ...prev, end_date: moment(date).format("YYYY-MM-DD") }))
                    }
                    months={1}
                    position="left"
                  />
                  )}
            </FormLayout.Group>
            <FormLayout.Group>
              <Checkbox
                id="display_online"
                size="medium"
                value={!!event?.display_online}
                checked={!!event?.display_online}
                onChange={() => setEvent(prev => ({ ...prev, display_online: !prev.display_online }))}
                label={t("Display Online")}
                supportText={t("Would you like to publicly display this event in the client portal list and calendar?")}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextEditor
                markdownText={event?.description ?? ""}
                markdownTextOnChange={description => setEvent(prev => ({ ...prev, description: description }))}
                label={t("secure.facility.hospitality.hospitality_event.015")}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>

      {event?.deposits?.length >= 1 && (
        <Card title={t("secure.facility.hospitality.hospitality_event.008")}>
          <Card.Section>
            <DataTable
              columns={[
                { label: t("secure.facility.hospitality.hospitality_event.009"), width: "25%" },
                { label: t("secure.facility.hospitality.hospitality_event.010"), width: "20%" },
                { label: t("secure.facility.hospitality.hospitality_event.017"), width: "10%" },
                { label: t("secure.facility.hospitality.hospitality_event.011"), width: "40%" },
                { label: "", width: "5%" },
              ]}
              loading={event.deposits === undefined}
            >
              {event.deposits
                ?.filter(deposit => deposit.kind !== 'refund')
                .map((deposit, index) => (
                  <tr key={index}>
                    <td>{moment(deposit.processed_at_local).format("YYYY-MM-DD")}</td>
                    <td>
                      <LocaleCurrency currency={deposit.currency} amount={deposit.amount} />
                    </td>
                    <td>{statusBadge(deposit)}</td>
                    <td>{deposit.note}</td>
                    <td>
                      <NavigationDropdownNew
                        showPlainTextLabel
                        rightAlign
                        label={<FontAwesomeIcon icon={["far", "chevron-down"]} />}
                        sections={[
                          [
                            {
                              icon: "print",
                              label: t("secure.facility.hospitality.hospitality_event.018"),
                              onClick: () => getDepositReceipt(deposit.id),
                            },
                          ],
                        ]}
                      />
                    </td>
                  </tr>
                ))}
            </DataTable>
          </Card.Section>
        </Card>
      )}
    </Page>
  );
}

const statusBadge = (deposit: IInvoiceTransaction) => {
  if (deposit.refund_transaction !== null) {
    return <Badge type="warning">{t("Refunded")}</Badge>;
  }

  if (deposit.invoice_id === null) {
    return <Badge type="success">{t("secure.facility.hospitality.hospitality_event.019")}</Badge>;
  }

  return <Badge type="gray">{t("secure.facility.hospitality.hospitality_event.020")}</Badge>;
};
